import { produce } from "immer";
import { Store } from "reducers/rootReducer";
import { of, from, Observable } from "rxjs";
import { AxiosRequestConfig, AxiosError } from "axios";
import { endpoints } from "config";
import { Scopes, loadTokenResource, saveTokenResource } from "helpers/auth";
import api from "api";
import {
  map,
  tap,
  switchMap,
  catchError,
  ignoreElements,
} from "rxjs/operators";
import { combineEpics, ofType, ActionsObservable } from "redux-observable";
import { createSelector } from "reselect";
import { addSeconds } from "date-fns";

export type SignInAttempt = {
  username: string;
  password: string;
  rememberMe: boolean;
  inviteToken: string | undefined;
};

type SignInError = {
  error: null | string;
  error_description: string;
  error_uri: null;
};

type SignInOkay = {
  details: {
    accessToken: string;
    expiresIn: string;
    refreshToken: string;
    refreshTokenExpiresIn: string;
    scopes?: Scopes;
    tokenType: string;
  };
  status: string;
};

type SignInResponse = SignInOkay | SignInError;

const isResponseOkay = (r: SignInResponse): r is SignInOkay =>
  (r as SignInOkay).status === "Success" || (r as SignInOkay).status === "1";

/* STATE */
type AuthStatus = "signed_out" | "signed_in" | "pending";
type State = {
  appVisible: boolean;
  authStatus: AuthStatus;
  message: string;
  invitedBy: string | null;
};

const initialState: State = {
  authStatus: "signed_out",
  appVisible: false,
  message: "",
  invitedBy: null,
};

/* ACTIONS */

const SIGN_OUT = "app/auth/SIGN_OUT";
const SIGN_IN = "app/auth/SIGN_IN";
const SIGN_IN_OKAY = "app/auth/SIGN_IN_OKAY";
const SIGN_IN_ERROR = "app/auth/SIGN_IN_ERROR";
const SHOW_APP = "app/auth/SHOW_APP";
const CHECK_SIGNED_IN = "app/auth/CHECK_SIGNED_IN";
const CLEAR_MESSAGE = "app/auth/CLEAR_MESSAGE";
const SET_INVITED_BY = "app/auth/SET_INVITED_BY";

type Action =
  | { type: typeof SIGN_IN; payload: SignInAttempt }
  | { type: typeof SIGN_OUT; message: string; signOutMarker: boolean }
  | { type: typeof CHECK_SIGNED_IN }
  | { type: typeof SHOW_APP; payload: boolean }
  | { type: typeof SIGN_IN_OKAY; payload: SignInOkay }
  | { type: typeof SIGN_IN_ERROR; payload: SignInError }
  | { type: typeof SET_INVITED_BY; payload: string }
  | { type: typeof CLEAR_MESSAGE };

/* REDUCER */

export default function reducer(state = initialState, action: Action): State {
  return produce(state, draft => {
    switch (action.type) {
      case SHOW_APP:
        draft.appVisible = true;
        // Is the user already signed in?
        if (action.payload === true) draft.authStatus = "signed_in";
        break;
      case SIGN_OUT:
        draft.authStatus = "signed_out";
        draft.message = action.message;
        break;
      case SIGN_IN:
        draft.authStatus = "pending";
        draft.message = "";
        break;
      case SIGN_IN_OKAY:
        draft.authStatus = "signed_in";
        break;
      case SIGN_IN_ERROR:
        draft.authStatus = "signed_out";
        draft.message = action.payload.error_description;
        break;
      case SET_INVITED_BY:
        draft.invitedBy = action.payload;
        break;
    }
  });
}

/* EPICS */

function makeRequest<T>(config: AxiosRequestConfig) {
  return from(api.request<T>(config));
}

function saveResponse(r: SignInResponse) {
  if (isResponseOkay(r)) {
    const {
      accessToken,
      refreshToken,
      expiresIn,
      scopes,
      refreshTokenExpiresIn,
    } = r.details;
    const refreshTokenExpires = addSeconds(
      new Date(),
      parseInt(refreshTokenExpiresIn, 10)
    );
    const tokenExpires = addSeconds(new Date(), parseInt(expiresIn, 10));

    saveTokenResource({
      accessToken,
      refreshToken,
      tokenExpires,
      refreshTokenExpires: refreshTokenExpires,
      scopes: scopes ?? [],
    });
  }
}

function checkResponse(r: SignInResponse) {
  if (isResponseOkay(r)) return signInOkay(r);
  return signInError(r);
}

const parseApiErrorMessageCode = (code: string): string => {
  switch (code) {
    case "Username_Or_Password_Incorrect":
      return "Invalid username or password";
    case "Account_Locked":
      return "Your account has been locked after too many failed attempts. Please try again in 10 minutes.";
    case "Account_Suppressed":
      return "Your account is currently inactive.";
    default:
      return "Oops, something went wrong. Please try again later.";
  }
};

/** Attempt to sign in and handle response, cancel if we sign out while a request pending. */
const signInEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(SIGN_IN),
    switchMap(attempt =>
      makeRequest<SignInResponse>({
        url: endpoints.auth.login,
        data: attempt.payload,
        method: "post",
      }).pipe(
        tap(res => saveResponse(res.data)),
        map(res => checkResponse(res.data)),
        catchError((err: AxiosError) => {
          console.log("err", err.response);
          const errorMessage = parseApiErrorMessageCode(
            err.response?.data.errors &&
              err.response.data.errors.length &&
              err.response.data.errors.length > 0
              ? err.response?.data.errors[0].messageCode
              : ""
          );
          return of(
            signInError({
              error: null,
              error_description: `${errorMessage}`,
              error_uri: null,
            })
          );
        })
        //takeUntil(action$.pipe(ofType(SIGN_OUT, SIGN_IN_ERROR)))
      )
    )
  );

/** Clear the local storage on sign out, so we don't appear logged in on refresh. */
const signOutEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(SIGN_OUT),
    tap(action => {
      window && window.localStorage.clear();
      if (action.signOutMarker) {
        window && window.localStorage.setItem("signOutAlert", "true");
      }
    }),
    ignoreElements()
  );

const checkSignedInEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(CHECK_SIGNED_IN),
    map(loadTokenResource),
    map(r => r !== null),
    map(showApp)
  );

export const authEpic = combineEpics(
  signInEpic,
  signOutEpic,
  checkSignedInEpic
);

/* ACTION CREATORS */

export const clearAuthMessage = (): Action => {
  return {
    type: CLEAR_MESSAGE,
  };
};

export const signIn = (payload: SignInAttempt): Action => ({
  type: SIGN_IN,
  payload,
});

export const signOut = (message = "", marker = true): Action => ({
  type: SIGN_OUT,
  message,
  signOutMarker: marker,
});

export const checkSignedIn = (): Action => ({
  type: CHECK_SIGNED_IN,
});

const signInOkay = (payload: SignInOkay): Action => ({
  type: SIGN_IN_OKAY,
  payload,
});

const signInError = (payload: SignInError): Action => ({
  type: SIGN_IN_ERROR,
  payload,
});

const showApp = (payload: boolean): Action => ({
  type: SHOW_APP,
  payload,
});

export const setInvitedBy = (id: string): Action => ({
  type: SET_INVITED_BY,
  payload: id,
});

/* SELECTORS */

export const selectAuthStatus = (app: Store) => app.auth.authStatus;
export const selectSignedIn = createSelector(
  selectAuthStatus,
  status => status === "signed_in"
);
export const selectAuthMessage = (app: Store) => app.auth.message;
export const selectAppVisible = (app: Store) => app.auth.appVisible;
export const selectInvitedBy = (app: Store) => app.auth.invitedBy;
